import {match} from 'ts-pattern';

import {AppLanguage} from '../types/AppLanguage';

type DateUnitLetters = {
  day: string;
  month: string;
  year: string;
};

/**
 * Localized abbreviations of date units, kept in sync with the mobile date inputs
 * (libs/platform/native/components/src/components/DateInput/utils/getDateFormatConfig.ts).
 */
const DATE_UNIT_LETTERS: Record<AppLanguage, DateUnitLetters> = {
  cs: {day: 'D', month: 'M', year: 'R'},
  de: {day: 'T', month: 'M', year: 'J'},
  en: {day: 'D', month: 'M', year: 'Y'},
  es: {day: 'D', month: 'M', year: 'A'},
  fr: {day: 'J', month: 'M', year: 'A'},
  it: {day: 'G', month: 'M', year: 'A'},
  pl: {day: 'D', month: 'M', year: 'R'},
  sk: {day: 'D', month: 'M', year: 'R'},
};

/** Hours, minutes and seconds share one abbreviation across all supported languages. */
const HOUR_LETTER = 'H';
const MINUTE_LETTER = 'M';
const SECOND_LETTER = 'S';
/** Only the English time format carries a day period, and date-fns renders it as AM / PM. */
const DAY_PERIOD_HINT = 'AM/PM';

const DATE_TIME_TOKEN_PATTERN = /d+|M+|y+|H+|h+|m+|s+|a+/g;

const TWO_DIGIT_YEAR_TOKEN = 'yy';
const FULL_YEAR_LENGTH = 4;

/**
 * Width of the year hint. Only `yy` renders two digits; every other year token renders the
 * whole year, so `dd.MM.y` expects four digits just like `dd.MM.yyyy`.
 */
const getYearLength = (token: string) =>
  token === TWO_DIGIT_YEAR_TOKEN ? TWO_DIGIT_YEAR_TOKEN.length : FULL_YEAR_LENGTH;

/**
 * Turns a date-fns pattern into a human readable placeholder in the given language,
 * e.g. `dd.MM.yyyy` becomes `DD.MM.RRRR` in Czech and `TT.MM.JJJJ` in German.
 *
 * Expects a numeric pattern such as `localeConfig.dateTimeFormat.dateShort` or `.timeShort`;
 * month-name tokens (`MMM`, `MMMM`) have no letter abbreviation and pass through unchanged.
 */
export function getDateTimeFormatPlaceholder(format: string, language: AppLanguage): string {
  const letters = DATE_UNIT_LETTERS[language];

  return format.replace(DATE_TIME_TOKEN_PATTERN, (token) =>
    match(token.charAt(0))
      .with('d', () => letters.day.repeat(token.length))
      .with('M', () => letters.month.repeat(token.length))
      .with('y', () => letters.year.repeat(getYearLength(token)))
      .with('H', 'h', () => HOUR_LETTER.repeat(token.length))
      .with('m', () => MINUTE_LETTER.repeat(token.length))
      .with('s', () => SECOND_LETTER.repeat(token.length))
      .with('a', () => DAY_PERIOD_HINT)
      .otherwise(() => token)
  );
}
